import type { MailboxDto, MailDiscovery } from '@bk/contracts';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useState, type FormEvent } from 'react';
import { api, errorMessage } from '../../lib/api';
import { useTenant, useTenantQuery } from '../../lib/useTenant';
import { SourceProgress } from './WorkProgress';
import { Badge, Button, Card, ErrorText } from '../../ui';

const INTERVALS = [
  [15, 'ogni 15 minuti'],
  [30, 'ogni 30 minuti'],
  [60, 'ogni ora'],
  [240, 'ogni 4 ore'],
  [1440, 'una volta al giorno'],
] as const;

const STATUS: Record<
  MailboxDto['status'],
  { label: string; tone: 'gray' | 'green' | 'red' | 'amber' }
> = {
  in_attesa: { label: 'in attesa', tone: 'gray' },
  attiva: { label: 'letta di continuo', tone: 'green' },
  errore: { label: 'errore', tone: 'red' },
  sospesa: { label: 'sospesa', tone: 'amber' },
};

const when = (iso: string | null) =>
  iso ? new Date(iso).toLocaleString('it-IT', { dateStyle: 'short', timeStyle: 'short' }) : 'mai';

/**
 * Caselle di posta dell'azienda: il sistema le legge di continuo (solo lettura) e ne ricava come lavora l'azienda,
 * come sono stati gestiti i casi passati e i documenti utili.
 */
export function MailboxesPanel({ startOpen = false }: { startOpen?: boolean }) {
  const { tenant, t, isManager } = useTenant();
  const queryClient = useQueryClient();
  const list = useTenantQuery<{ data: MailboxDto[] }>(['mailboxes'], '/mailboxes', {
    enabled: isManager,
    refetchInterval: (d) => (d?.data.some((m) => m.syncing) ? 4000 : false),
  });
  const [open, setOpen] = useState(startOpen);
  const refresh = () =>
    void queryClient.invalidateQueries({ queryKey: ['tenant', tenant.id, 'mailboxes'] });
  const act = useMutation({
    mutationFn: ({
      m,
      action,
      body,
    }: {
      m: MailboxDto;
      action: 'sync' | 'patch' | 'delete';
      body?: unknown;
    }) =>
      action === 'sync'
        ? api(t(`/mailboxes/${m.id}/sync`), { method: 'POST' })
        : action === 'patch'
          ? api(t(`/mailboxes/${m.id}`), { method: 'PATCH', body })
          : api(t(`/mailboxes/${m.id}`), { method: 'DELETE' }),
    onSuccess: refresh,
  });
  if (!isManager) return null;
  const boxes = list.data?.data ?? [];
  return (
    <Card>
      <div className="flex flex-wrap items-center gap-2">
        <h2 className="font-semibold">Caselle di posta</h2>
        <span className="text-xs text-ink/50">
          le leggo di continuo (solo lettura): capisco come lavorate, come avete gestito i casi
          passati e trovo i documenti utili
        </span>
        <span className="flex-1" />
        {!open && <Button onClick={() => setOpen(true)}>Collega una casella</Button>}
      </div>
      <ErrorText>
        {list.error ? errorMessage(list.error) : act.error ? errorMessage(act.error) : ''}
      </ErrorText>
      {boxes.length > 0 && (
        <ul className="mt-3 divide-y divide-black/5">
          {boxes.map((m) => {
            const st = STATUS[m.status];
            const r = m.last_result;
            return (
              <li key={m.id} className="py-3 text-sm">
                <div className="flex flex-wrap items-center gap-2">
                  <span className="font-medium">{m.address}</span>
                  <Badge tone={st.tone}>{m.syncing ? 'lettura in corso…' : st.label}</Badge>
                  <span className="flex-1" />
                  <select
                    className="rounded border border-black/15 px-1 py-0.5 text-xs"
                    value={m.interval_minutes}
                    onChange={(e) =>
                      act.mutate({
                        m,
                        action: 'patch',
                        body: { interval_minutes: Number(e.target.value) },
                      })
                    }
                    aria-label="Ogni quanto leggere"
                  >
                    {INTERVALS.map(([v, label]) => (
                      <option key={v} value={v}>
                        {label}
                      </option>
                    ))}
                  </select>
                  {m.status !== 'sospesa' && (
                    <Button
                      variant="ghost"
                      disabled={m.syncing || act.isPending}
                      onClick={() => act.mutate({ m, action: 'sync' })}
                    >
                      Leggi ora
                    </Button>
                  )}
                  <Button
                    variant="ghost"
                    disabled={act.isPending}
                    onClick={() =>
                      act.mutate({
                        m,
                        action: 'patch',
                        body: { status: m.status === 'sospesa' ? 'attiva' : 'sospesa' },
                      })
                    }
                  >
                    {m.status === 'sospesa' ? 'Riattiva' : 'Sospendi'}
                  </Button>
                  <Button
                    variant="ghost"
                    disabled={act.isPending}
                    onClick={() =>
                      confirm(
                        `Scollegare ${m.address}? I messaggi già letti restano nell’archivio.`,
                      ) && act.mutate({ m, action: 'delete' })
                    }
                  >
                    Scollega
                  </Button>
                </div>
                <p className="mt-1 text-xs text-ink/55">
                  Ultima lettura: {when(m.last_sync_at)}
                  {r?.analizzati !== undefined && ` · ${r.analizzati.toLocaleString('it-IT')} messaggi letti in tutto`}
                  {r?.storico_completo && ' · storico letto, ora leggo solo i nuovi'}
                </p>
                {/* Coda di lavorazione (ADR 0043): conteggio, scarico, lettura e indice, con il tempo stimato. */}
                {m.progress && (m.progress.status === 'in_corso' || m.progress.errors > 0) && (
                  <div className="mt-2">
                    <SourceProgress source={m.progress} title={m.progress.initial_done ? 'Lettura dei messaggi nuovi' : 'Lettura di tutta la casella'} />
                  </div>
                )}
                {m.last_error && <p className="text-xs text-red-700">{m.last_error}</p>}
              </li>
            );
          })}
        </ul>
      )}
      {open && (
        <MailboxForm
          onDone={() => {
            setOpen(false);
            refresh();
          }}
          onCancel={() => setOpen(false)}
        />
      )}
    </Card>
  );
}

export function MailboxForm({ onDone, onCancel, initialAddress = '' }: { onDone: () => void; onCancel: () => void; initialAddress?: string }) {
  const { t } = useTenant();
  const [address, setAddress] = useState(initialAddress);
  const [found, setFound] = useState<MailDiscovery | null>(null);
  const [company, setCompany] = useState(false);
  const [method, setMethod] = useState<'oauth' | 'password'>('password');
  const [server, setServer] = useState({ host: '', port: 993, secure: true });
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [client, setClient] = useState({ id: '', secret: '' });
  const [interval, setEvery] = useState(60);

  const discover = useMutation({
    mutationFn: () => api<MailDiscovery>(t('/mailboxes/discover'), { body: { address } }),
    onSuccess: (d) => {
      setFound(d);
      setServer({ host: d.host ?? '', port: d.port, secure: d.secure });
      setUsername(d.address);
      // Microsoft 365 non accetta più la password su IMAP: si accede dal browser.
      setMethod(d.provider ? 'oauth' : 'password');
    },
  });
  const provider = found?.provider ?? null;
  const input = () => ({
    address: found!.address,
    aziendale: company,
    auth:
      method === 'oauth'
        ? provider === 'microsoft'
          ? 'oauth_microsoft'
          : 'oauth_google'
        : 'password',
    host: server.host,
    port: server.port,
    secure: server.secure,
    username,
    interval_minutes: interval,
    ...(method === 'password' ? { password } : {}),
    ...(method === 'oauth' && !found?.oauth_ready
      ? { client_id: client.id, client_secret: client.secret }
      : {}),
  });
  const save = useMutation({
    mutationFn: async () => {
      if (method === 'oauth') {
        const { url } = await api<{ url: string }>(t('/mailboxes/oauth/start'), { body: input() });
        window.location.href = url;
        return;
      }
      await api<MailboxDto>(t('/mailboxes'), { body: input() });
      onDone();
    },
  });
  const ready =
    !!found &&
    !!server.host &&
    !!username &&
    company &&
    (method === 'oauth' ? found.oauth_ready || !!client.id : !!password);
  const submit = (e: FormEvent) => {
    e.preventDefault();
    if (!found) discover.mutate();
    else if (ready) save.mutate();
  };
  const field = 'w-full rounded-lg border border-black/15 px-2 py-1.5 text-sm';

  return (
    <form className="mt-4 space-y-3 rounded-lg bg-black/[0.03] p-4 text-sm" onSubmit={submit}>
      <label className="block">
        <span className="mb-1 block text-xs font-medium text-ink/60">Indirizzo della casella</span>
        <div className="flex gap-2">
          <input
            className={field}
            type="email"
            placeholder="Indirizzo della casella aziendale"
            value={address}
            onChange={(e) => {
              setAddress(e.target.value);
              setFound(null);
            }}
          />
          {!found && (
            <Button type="submit" disabled={!address.includes('@') || discover.isPending}>
              {discover.isPending ? 'Cerco…' : 'Avanti'}
            </Button>
          )}
        </div>
      </label>
      <ErrorText>{discover.error ? errorMessage(discover.error) : ''}</ErrorText>
      {found && (
        <>
          <p className="text-xs text-ink/60">
            {found.host
              ? `Server trovato: ${found.host}${provider ? ` (${provider === 'microsoft' ? 'Microsoft 365' : 'Google'})` : ''}.`
              : 'Non ho trovato il server da solo: indicalo qui sotto (lo trovi nelle impostazioni della posta o chiedendo al fornitore).'}
          </p>
          <label className="flex items-start gap-2 text-xs text-ink/70">
            <input
              type="checkbox"
              className="mt-0.5"
              checked={company}
              onChange={(e) => setCompany(e.target.checked)}
            />
            <span>
              È una casella dell’azienda, di un ufficio o di una funzione, non intestata a una
              persona. Le caselle delle persone non si collegano.
            </span>
          </label>
          {provider && (
            <fieldset className="space-y-1">
              <legend className="mb-1 text-xs font-medium text-ink/60">Come si accede</legend>
              <label className="flex items-center gap-2">
                <input
                  type="radio"
                  checked={method === 'oauth'}
                  onChange={() => setMethod('oauth')}
                />{' '}
                Con l’account {provider === 'microsoft' ? 'Microsoft' : 'Google'}, dal browser
                (consigliato)
              </label>
              {provider === 'google' && (
                <label className="flex items-center gap-2">
                  <input
                    type="radio"
                    checked={method === 'password'}
                    onChange={() => setMethod('password')}
                  />{' '}
                  Con una password per le app
                </label>
              )}
            </fieldset>
          )}
          {method === 'oauth' && !found.oauth_ready && (
            <div className="space-y-2 text-xs text-ink/65">
              <p>
                Serve un’applicazione registrata dall’azienda presso{' '}
                {provider === 'microsoft'
                  ? 'Microsoft (Entra ID, "Registrazioni app")'
                  : 'Google Cloud'}{' '}
                con l’indirizzo di ritorno{' '}
                <code className="rounded bg-white px-1">{found.oauth_redirect_uri}</code>.
              </p>
              <input
                className={field}
                placeholder="ID client (client id)"
                value={client.id}
                onChange={(e) => setClient({ ...client, id: e.target.value })}
              />
              <input
                className={field}
                type="password"
                placeholder="Segreto client (client secret)"
                value={client.secret}
                onChange={(e) => setClient({ ...client, secret: e.target.value })}
              />
            </div>
          )}
          {method === 'password' && (
            <div className="grid gap-2 sm:grid-cols-[1fr_6rem]">
              <input
                className={field}
                placeholder="Server IMAP (es. imap.fornitore.it)"
                value={server.host}
                onChange={(e) => setServer({ ...server, host: e.target.value.trim() })}
              />
              <input
                className={field}
                type="number"
                placeholder="Porta"
                value={server.port}
                onChange={(e) => setServer({ ...server, port: Number(e.target.value) })}
              />
              <label className="flex items-center gap-2 text-xs text-ink/60 sm:col-span-2">
                <input
                  type="checkbox"
                  checked={server.secure}
                  onChange={(e) => setServer({ ...server, secure: e.target.checked })}
                />{' '}
                Connessione cifrata diretta (SSL/TLS, di solito porta 993; senza spunta si usa
                STARTTLS)
              </label>
              <input
                className={field}
                placeholder="Utente (di solito l’indirizzo)"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
              />
              <span />
              <input
                className={field}
                type="password"
                autoComplete="new-password"
                placeholder={provider === 'google' ? 'Password per le app' : 'Password'}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
              />
            </div>
          )}
          <label className="flex items-center gap-2 text-xs text-ink/60">
            Leggi
            <select
              className="rounded border border-black/15 px-1 py-0.5"
              value={interval}
              onChange={(e) => setEvery(Number(e.target.value))}
            >
              {INTERVALS.map(([v, label]) => (
                <option key={v} value={v}>
                  {label}
                </option>
              ))}
            </select>
          </label>
          <p className="text-xs text-ink/50">
            Il sistema legge soltanto: non invia, non sposta e non segna come letti i messaggi. La
            prima volta legge i più recenti, poi un po’ di storico a ogni giro.
          </p>
          <ErrorText>{save.error ? errorMessage(save.error) : ''}</ErrorText>
          <div className="flex gap-2">
            <Button type="submit" disabled={!ready || save.isPending}>
              {save.isPending
                ? 'Provo l’accesso…'
                : method === 'oauth'
                  ? `Accedi con ${provider === 'microsoft' ? 'Microsoft' : 'Google'}`
                  : 'Collega'}
            </Button>
            <Button type="button" variant="ghost" onClick={onCancel}>
              Annulla
            </Button>
          </div>
        </>
      )}
    </form>
  );
}
